const META_INSTRUCTION_MARKERS = [
  /analyze the provided pdf/i,
  /generate a professional campaign/i,
  /create a logical storyboard/i,
  /\bcreate a campaign\b/i,
  /\bgenerate a video\b/i,
  /\bmake it cinematic\b/i,
  /\bthink like a creative director\b/i,
  /\badd voiceover\b/i,
  /\buse uploaded assets\b/i,
  /\bcreate engaging scenes\b/i,
  /\bdo not summarize\b/i,
];

function isMetaInstructionPrompt(prompt = '') {
  const text = String(prompt || '').trim();
  if (!text) {
    return false;
  }
  const markerHits = META_INSTRUCTION_MARKERS.filter((pattern) => pattern.test(text)).length;
  return markerHits >= 2 || (text.length > 100 && markerHits >= 1);
}

function titleCaseWords(value = '') {
  return String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function deriveTitleFromFilename(files = []) {
  const pdfFile = files.find((file) => /\.pdf$/i.test(String(file?.name || '')));
  if (!pdfFile?.name) {
    return '';
  }
  const stem = String(pdfFile.name).replace(/\.pdf$/i, '').replace(/[-_]+/g, ' ').trim();
  return titleCaseWords(stem);
}

function deriveTitleFromPrompt(userPrompt = '') {
  const text = String(userPrompt || '').trim();
  if (!text || isMetaInstructionPrompt(text)) {
    return '';
  }

  const quoted = text.match(/["']([^"']{3,60})["']/);
  if (quoted?.[1]) {
    return quoted[1].trim();
  }

  const campaignForMatch = text.match(/\bcampaign for (.+?)(?:[.!?\n]|$)/i);
  if (campaignForMatch?.[1]) {
    return titleCaseWords(campaignForMatch[1].slice(0, 60));
  }

  const advertisementForMatch = text.match(/\badvertisement for (.+?)(?:[.!?\n]|$)/i);
  if (advertisementForMatch?.[1]) {
    return titleCaseWords(advertisementForMatch[1].slice(0, 60));
  }

  const aboutMatch = text.match(/\babout (.+?)(?:[.!?\n]|$)/i);
  if (aboutMatch?.[1]) {
    return titleCaseWords(aboutMatch[1].slice(0, 60));
  }

  const createMatch = text.match(/\bcreate (?:a |an )?(.+?) campaign\b/i);
  if (createMatch?.[1] && !/professional|promotional|marketing|video/i.test(createMatch[1])) {
    return titleCaseWords(createMatch[1].slice(0, 60));
  }

  if (text.length <= 60) {
    return text;
  }

  const firstSentence = text.split(/[.!?\n]/).map((part) => part.trim()).find(Boolean) || text;
  return firstSentence.slice(0, 60).trim();
}

function deriveArchetype({ prompt = '', files = [] }) {
  const haystack = [
    prompt,
    files.map((file) => file?.name || '').join(' '),
  ].join(' ').toLowerCase();

  if (/wedding|marriage|bride|groom/.test(haystack)) {
    return 'wedding';
  }
  if (/travel|trip|itinerary|destination/.test(haystack)) {
    return 'travel';
  }
  if (/product|shop|store|ecommerce|cart/.test(haystack)) {
    return 'product';
  }
  if (/event|conference|webinar|summit/.test(haystack)) {
    return 'event';
  }
  if (/corporate|enterprise|business|company/.test(haystack)) {
    return 'corporate';
  }
  return 'promotional';
}

const ARCHETYPE_TITLE_LABELS = {
  wedding: 'Wedding Campaign',
  travel: 'Travel Campaign',
  product: 'Product Campaign',
  event: 'Event Campaign',
  corporate: 'Corporate Campaign',
  promotional: 'Promotional Campaign',
};

const ASPECT_RATIO_HINTS = [
  { ratio: '9:16', patterns: [/instagram reel/i, /\btiktok\b/i, /\breels?\b/i, /\bshorts\b/i, /\bvertical\b/i, /\b9:16\b/, /\bportrait video\b/i, /\bstories?\b/i] },
  { ratio: '1:1', patterns: [/\bsquare\b/i, /\b1:1\b/, /instagram post/i] },
  { ratio: '16:9', patterns: [/\blandscape\b/i, /\b16:9\b/, /\byoutube\b/i, /\bwebsite\b/i] },
];

function resolveAspectRatio({
  prompt = '',
  files = [],
  campaignArchetype = '',
  currentAspectRatio = '16:9',
}) {
  const haystack = [
    prompt,
    campaignArchetype,
    files.map((file) => file?.name || '').join(' '),
  ].join(' ').toLowerCase();

  for (const hint of ASPECT_RATIO_HINTS) {
    if (hint.patterns.some((pattern) => pattern.test(haystack))) {
      return hint.ratio;
    }
  }

  const archetype = String(campaignArchetype || deriveArchetype({ prompt, files })).toLowerCase();
  if (archetype === 'travel' && /\bsocial|instagram|tiktok|reel|mobile\b/.test(haystack)) {
    return '9:16';
  }
  if (archetype === 'product' && /\bsocial|instagram|square|feed\b/.test(haystack)) {
    return '1:1';
  }

  if (['16:9', '9:16', '1:1'].includes(String(currentAspectRatio || '').trim())) {
    return String(currentAspectRatio).trim();
  }

  return '16:9';
}

/**
 * Infer smart campaign title and aspect ratio from prompt, uploaded files, and campaign type.
 * @param {{ prompt?: string, files?: File[], currentTitle?: string, currentAspectRatio?: string }} params
 */
export function inferSmartCampaignDefaults({
  prompt = '',
  files = [],
  currentTitle = '',
  currentAspectRatio = '16:9',
}) {
  const campaignArchetype = deriveArchetype({ prompt, files });
  const fromFilename = deriveTitleFromFilename(files);
  const fromPrompt = deriveTitleFromPrompt(prompt);
  const title = String(currentTitle || '').trim()
    || fromFilename
    || fromPrompt
    || ARCHETYPE_TITLE_LABELS[campaignArchetype]
    || '';

  return {
    campaignArchetype,
    title,
    aspectRatio: resolveAspectRatio({
      prompt,
      files,
      campaignArchetype,
      currentAspectRatio,
    }),
  };
}

export {
  deriveArchetype,
  deriveTitleFromPrompt,
  resolveAspectRatio,
};
